import React, { createContext, useContext, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./ProfileMenuPopover.scss";
import { Popover } from "../popover/Popover";
import { ReactComponent as MoreIcon } from "../icons/More.svg";
import { useIntl, FormattedMessage, defineMessage } from "react-intl";

function MoreMenuItem({ item, closePopover }) {
  const Icon = item.icon;

  return (
    <li onClick={closePopover}>
      {item.href ? (
        <a
          className={classNames(styles.moreMenuItemTarget, "link")}
          href={item.href}
          target={item.target || "_blank"}
          rel="noopener noreferrer"
          onClick={event => item.onClick(item, event)}
        >
          <Icon />
          <span>{item.label}</span>
        </a>
      ) : (
        <button className={styles.moreMenuItemTarget} onClick={event => item.onClick(item, event)}>
          <Icon />
          <span>{item.label}</span>
        </button>
      )}
    </li>
  );
}

MoreMenuItem.propTypes = {
  item: PropTypes.shape({
    href: PropTypes.string,
    target: PropTypes.string,
    icon: PropTypes.elementType.isRequired,
    label: PropTypes.node.isRequired,
    onClick: PropTypes.func
  }).isRequired,
  closePopover: PropTypes.func.isRequired
};

function MoreMenuGroup({ group, closePopover }) {
  return (
    <li>
      <ul className={styles.moreMenuItemList}>
        {group.items.map(item => (
          <MoreMenuItem key={item.id} item={item} closePopover={closePopover} />
        ))}
      </ul>
    </li>
  );
}

MoreMenuGroup.propTypes = {
  group: PropTypes.object.isRequired,
  closePopover: PropTypes.func.isRequired
};

function MoreMenuPopoverContent({ menu, closePopover, person, email, onEditAvatar, setVisible }) {
  return (
    <div className={styles.moreMenuPopover}>
      <div className={styles.profileInfoContainer}>
        <div className={styles.profileInfoSubcontainer}>
          <h5 className={styles.profileInfoName} id="profile-menu-name">
            {person.displayName}
          </h5>
          <span className={styles.profileInfoName} id="profile-menu-email">
            {email}
          </span>

          <a
            onClick={() => {
              onEditAvatar();
              setVisible(false);
            }}
          >
            <FormattedMessage id="profile-menu-edit" defaultMessage="Edit avatar" />
          </a>
        </div>
      </div>
      <ul>
        {menu.map((group, i) => (
          <MoreMenuGroup key={`group-${i}`} group={group} closePopover={closePopover} />
        ))}
      </ul>
    </div>
  );
}

MoreMenuPopoverContent.propTypes = {
  menu: PropTypes.array.isRequired,
  closePopover: PropTypes.func.isRequired,
  person: PropTypes.object.isRequired,
  email: PropTypes.string.isRequired,
  onEditAvatar: PropTypes.func,
  setVisible: PropTypes.func
};

// The MoreMenuContext allows us to control the more menu popover visibility from the MoreMenuPopoverButton
// and CompactMoreMenuButton.
const ProfileMenuContext = createContext([false, () => {}]);

export function ProfileMenuContextProvider({ initiallyVisible, children }) {
  const context = useState(initiallyVisible || false);
  return <ProfileMenuContext.Provider value={context}>{children}</ProfileMenuContext.Provider>;
}

ProfileMenuContextProvider.propTypes = {
  initiallyVisible: PropTypes.bool,
  children: PropTypes.node
};

const moreMenuTitle = defineMessage({
  id: "more-menu-popover.title",
  defaultMessage: "More"
});

export function ProfileMenuPopoverButton({ menu, childrenElement, person, email, onEditAvatar }) {
  const [visible, setVisible] = useContext(ProfileMenuContext);
  return (
    <Popover
      content={props => (
        (childProps) =>
        <MoreMenuPopoverContent
          setVisible={setVisible}
          menu={menu}
          onEditAvatar={onEditAvatar}
          person={person}
          email={email}
          {...props}
          {...childProps}
        />
      )}
      placement="bottom"
      showHeader={true}
      customHeaderStyle={styles.profileHeader}
      popoverClass={styles.profileMenu}
      isVisible={visible}
      title=""
      onChangeVisible={setVisible}
    >
      {({ togglePopover, popoverVisible, triggerRef }) => (
        <div>
          <div ref={triggerRef} selected={popoverVisible} onClick={togglePopover}>
            {childrenElement}
          </div>
        </div>
      )}
    </Popover>
  );
}

ProfileMenuPopoverButton.propTypes = {
  menu: PropTypes.array.isRequired,
  childrenElement: PropTypes.node,
  person: PropTypes.object,
  email: PropTypes.string,
  onEditAvatar: PropTypes.func,
  isMobile: PropTypes.bool
};

// The CompactMoreMenuButton is only shown in the small breakpoint.
// We actually render the popover in the MoreMenuPopoverButton so that when resizing the window,
// the popover positions itself relative to the correct element.
export function CompactMoreMenuButton({ className, ...rest }) {
  const intl = useIntl();
  const [, setVisible] = useContext(ProfileMenuContext);

  return (
    <button
      className={classNames(styles.compactButton, className)}
      aria-label={intl.formatMessage(moreMenuTitle)}
      onClick={e => {
        // Stop event bubbling so we don't immediately close the popover by clicking outside it.
        e.stopPropagation();
        setVisible(true);
      }}
      {...rest}
    >
      <MoreIcon />
    </button>
  );
}

CompactMoreMenuButton.propTypes = {
  className: PropTypes.string
};
